/**
 * Screen IDs — one fixed reference number for every screen in a session.
 *
 * WHY THIS EXISTS
 * Andy and Marta report problems by screenshot: "the wording is off on 3.2".
 * Until now the number under each screen was a hand-typed "2 of 4", written
 * separately on each screen. Screens were added later without one (Feel had
 * six screens and said "of 5" on two of them), so the numbers stopped meaning
 * anything. This file is the single list, and each session component maps its
 * own states onto it through a `Record` that TypeScript checks: add a state
 * without giving it a screen and `tsc -b` fails.
 *
 * HOW TO READ AN ID
 *   3.2    phase 3 (See), second screen of the standard path
 *   2.R3   phase 2 (Feel), third screen of the RAIN lens
 *   3.W2   phase 3 (See), second screen of The Work lens
 *   3.C2   the insight card shown in phase 3, second screen
 *   6.G1   the grounding exercise offered in phase 6, first screen
 *   4.0    the "something went wrong" screen of phase 4
 *
 * Phase 0 is everything before Ground: the intention, the emotion picker, the
 * lens picker, and the two screens a free account can be stopped at.
 *
 * THE RULES
 * - An ID belongs to a screen forever. It never depends on the path taken to
 *   reach it: a lens or a skip that bypasses a screen bypasses its number too.
 * - Never renumber. A new screen takes the next free number in its group, even
 *   if that puts it out of visual order; a removed screen retires its number.
 *   A number that moved would make every old bug report point at the wrong
 *   screen.
 * - Plain characters, the same in every language. This is a reference, not
 *   copy, so it is deliberately not in the message catalogue.
 * - Letter groups (R, W, C, G) keep every number to one digit, so "2.1" can
 *   never be misheard or mistyped as "2.10".
 */

export const SCREENS = {
  // ── Phase 0 — before the session proper ───────────────────────────────────
  'intention':            '0.1',
  'series.intro':         '0.2',
  'arrival.emotion':      '0.3',
  'arrival.warning':      '0.4',
  'arrival.lens':         '0.5',
  'gate.cooldown':        '0.6',
  'gate.limit':           '0.7',

  // ── Phase 1 — Ground ──────────────────────────────────────────────────────
  'ground.error':         '1.0',
  'ground.story':         '1.1',
  'ground.mirror':        '1.2',

  // ── Phase 2 — Feel ────────────────────────────────────────────────────────
  'feel.error':           '2.0',
  'feel.feeling':         '2.1',
  'feel.body':            '2.2',
  'feel.somatic':         '2.3',
  'feel.belief':          '2.4',
  'feel.expression':      '2.5',
  'feel.reflection':      '2.6',
  'feel.rain.recognize':  '2.R1',
  'feel.rain.allow':      '2.R2',
  'feel.rain.investigate':'2.R3',
  'feel.rain.nurture':    '2.R4',

  // ── Phase 3 — See ─────────────────────────────────────────────────────────
  'see.error':            '3.0',
  'see.belief':           '3.1',
  'see.pattern':          '3.2',
  'see.deepening':        '3.3',
  'see.ownWords':         '3.4',
  'see.ownWordsMirror':   '3.5',
  'see.collapse':         '3.6',
  'see.satori':           '3.7',
  'see.work.belief':      '3.W1',
  'see.work.q1':          '3.W2',
  'see.work.q2':          '3.W3',
  'see.work.q3':          '3.W4',
  'see.work.q4':          '3.W5',
  // 3.W6 is retired: the Work's "complete" state was never reached or drawn and
  // was removed (#70). The entry stays so the number is never handed out again.
  'see.work.complete':    '3.W6',
  'see.card.error':       '3.C0',
  'see.card.breath':      '3.C1',
  'see.card.card':        '3.C2',
  'see.card.integration': '3.C3',

  // ── Phase 4 — Shift ───────────────────────────────────────────────────────
  'shift.error':          '4.0',
  'shift.feeling':        '4.1',
  'shift.reframe':        '4.2',
  'shift.cardChoice':     '4.3',
  'shift.deepening':      '4.4',
  'shift.reflection':     '4.5',
  'shift.work.turnaround':'4.W1',
  // 4.W2 is retired for the same reason as 3.W6 (#70).
  'shift.work.complete':  '4.W2',
  'shift.card.error':     '4.C0',
  'shift.card.breath':    '4.C1',
  'shift.card.card':      '4.C2',
  'shift.card.integration':'4.C3',

  // ── Phase 5 — Choose ──────────────────────────────────────────────────────
  'choose.error':         '5.0',
  'choose.cardOffer':     '5.1',
  'choose.possibility':   '5.2',
  'choose.sedona':        '5.3',
  'choose.choice':        '5.4',
  'choose.artifact':      '5.5',
  'choose.identity':      '5.6',
  'choose.landing.practice':'5.7',
  'choose.landing.closing': '5.8',
  'choose.card.error':    '5.C0',
  'choose.card.breath':   '5.C1',
  'choose.card.card':     '5.C2',
  'choose.card.integration':'5.C3',

  // ── Phase 6 — Close ───────────────────────────────────────────────────────
  'close.error':          '6.0',
  'close.changed':        '6.1',
  'close.summary':        '6.2',
  'close.ritual':         '6.3',
  'close.ground.choose':  '6.G1',
  'close.ground.breath':  '6.G2',
  'close.ground.senses':  '6.G3',
  'close.ground.feet':    '6.G4',
  'close.ground.done':    '6.G5',
} as const

/** The name a component uses for a screen, e.g. `'see.pattern'`. */
export type ScreenKey = keyof typeof SCREENS

/** What is shown on screen, e.g. `'3.2'`. */
export type ScreenId = (typeof SCREENS)[ScreenKey]

/**
 * The shape every ID must have: a phase digit, a dot, then either one digit or
 * a group letter and one digit. Exported for the test that guards the list.
 */
export const SCREEN_ID_PATTERN = /^[0-6]\.(?:[0-9]|[RWCG][0-9])$/
